import React from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Box,
  Tooltip
} from '@mui/material';
import {
  Info as InfoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Student } from '@/types';
import { AttendanceCode } from '@/types/attendance';

interface AttendanceHistoryProps {
  student: Student;
}

const getStatusColor = (code: AttendanceCode) => {
  switch (code) {
    case 'P':
      return 'success';
    case 'A':
    case 'U':
      return 'error';
    case 'E':
      return 'info';
    case 'T':
    case 'ET':
    case 'UT':
      return 'warning';
    case 'S':
      return 'secondary';
    default:
      return 'default';
  }
};

const getStatusLabel = (code: AttendanceCode) => {
  switch (code) {
    case 'P':
      return 'Present';
    case 'A':
      return 'Absent';
    case 'E':
      return 'Excused';
    case 'U':
      return 'Unexcused';
    case 'T':
      return 'Tardy';
    case 'ET':
      return 'Excused Tardy';
    case 'UT':
      return 'Unexcused Tardy';
    case 'S':
      return 'School Activity';
    default:
      return code;
  }
};

export const AttendanceHistory: React.FC<AttendanceHistoryProps> = ({ student }) => {
  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6">Attendance History</Typography>
      </Box>

      <TableContainer sx={{ flexGrow: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Check In</TableCell>
              <TableCell>Check Out</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {student.attendanceRecords?.map((record) => (
              <TableRow key={record.id}>
                <TableCell>
                  {format(new Date(record.date), 'MMM d, yyyy')}
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(record.status as AttendanceCode)}
                    color={getStatusColor(record.status as AttendanceCode)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {record.arrivalTime || '—'}
                </TableCell>
                <TableCell>
                  {record.departureTime || '—'}
                </TableCell>
                <TableCell>
                  {record.notes ? (
                    <Tooltip title={record.notes}>
                      <IconButton size="small">
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : '—'}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {(!student.attendanceRecords || student.attendanceRecords.length === 0) && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No attendance records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};